import { blobToBase64 } from '../utils/blob-utils'
import {
    GmailFullMessageResponse,
    GmailMessageListResponse,
    GmailMessagePayloadPart,
    GmailMessagePayloadPartBody,
    GoogleUser,
    ListGmailMessagesParams,
} from './types'

export async function getUserProfile(accessToken: string): Promise<GoogleUser> {
    const response = await fetch(
        'https://www.googleapis.com/oauth2/v2/userinfo',
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        },
    )

    if (!response.ok) {
        throw new Error('Failed to fetch user profile')
    }

    const userData = (await response.json()) as GoogleUser
    return {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
    }
}

export async function listGmailMessages(
    accessToken: string,
    params: ListGmailMessagesParams = {},
): Promise<GmailMessageListResponse> {
    const baseUrl = new URL(
        'https://gmail.googleapis.com/gmail/v1/users/me/messages',
    )

    const searchParams = new URLSearchParams()
    if (params.q && params.q.trim().length > 0) {
        searchParams.set('q', params.q.trim())
    }
    if (params.pageToken) {
        searchParams.set('pageToken', params.pageToken)
    }
    if (params.maxResults && params.maxResults > 0) {
        searchParams.set('maxResults', String(params.maxResults))
    }
    if (params.labelIds && params.labelIds.length > 0) {
        for (const labelId of params.labelIds) {
            searchParams.append('labelIds', labelId)
        }
    }
    if (params.includeSpamTrash != null) {
        searchParams.set('includeSpamTrash', String(params.includeSpamTrash))
    }

    baseUrl.search = searchParams.toString()

    const response = await fetch(baseUrl, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    })

    if (!response.ok) {
        throw new Error(
            `Gmail API error: ${response.status} ${response.statusText}`,
        )
    }

    const data = (await response.json()) as GmailMessageListResponse

    return {
        messages: data.messages ?? [],
        nextPageToken: data.nextPageToken,
        resultSizeEstimate: data.resultSizeEstimate,
    }
}

export async function getGmailMessage(
    accessToken: string,
    params: {
        messageId: string
    },
): Promise<GmailFullMessageResponse> {
    const url = new URL(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${params.messageId}`,
    )
    url.searchParams.set('format', 'full')

    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
    })
    if (!response.ok) {
        throw new Error(
            `Gmail API error: ${response.status} ${response.statusText}`,
        )
    }
    return (await response.json()) as GmailFullMessageResponse
}

export async function downloadGmailAttachment(
    accessToken: string,
    params: {
        messageId: string
        attachmentId: string
    },
): Promise<GmailMessagePayloadPartBody> {
    const url = new URL(
        `https://gmail.googleapis.com/gmail/v1/users/me/messages/${params.messageId}/attachments/${params.attachmentId}`,
    )
    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${accessToken}` },
    })
    if (!response.ok) {
        throw new Error(
            `Gmail API error: ${response.status} ${response.statusText}`,
        )
    }

    const data = await response.json()
    // Gmail returns base64url encoded data
    const base64 = data.data.replace(/-/g, '+').replace(/_/g, '/')
    const byteString = atob(base64)
    const byteArray = new Uint8Array(byteString.length)
    for (let i = 0; i < byteString.length; i++) {
        byteArray[i] = byteString.charCodeAt(i)
    }
    // Don't hardcode the blob type - let the processing logic handle it
    const blob = new Blob([byteArray])

    const returnData = {
        data: await blobToBase64(blob),
        size: blob.size,
        attachmentId: data.attachmentId,
    } as GmailMessagePayloadPartBody

    return returnData
}

export async function batchGetGmailMessages(
    accessToken: string,
    params: {
        messageIds: string[]
    },
): Promise<Array<GmailFullMessageResponse | null>> {
    if (params.messageIds.length === 0) return []

    const batchEndpoint = 'https://gmail.googleapis.com/batch/gmail/v1'
    const boundaryPrefix = `batch_${Math.random().toString(36).slice(2)}`
    const BATCH_LIMIT = 10

    function decodeBase64Url(str: string): string {
        try {
            return decodeURIComponent(
                escape(window.atob(str.replace(/-/g, '+').replace(/_/g, '/'))),
            )
        } catch {
            return str
        }
    }

    function decodeMessageBodies(
        msg: GmailFullMessageResponse,
    ): GmailFullMessageResponse {
        const walk = (part: GmailMessagePayloadPart) => {
            if (typeof part !== 'object') return
            if (part.body && typeof part.body.data === 'string') {
                part.body.decodedData = decodeBase64Url(part.body.data)
            }
            if (Array.isArray(part.parts)) {
                for (const p of part.parts) walk(p)
            }
        }
        if (msg && msg.payload) walk(msg.payload)
        return msg
    }

    async function sendBatch(ids: string[], boundary: string) {
        const body =
            ids
                .map(
                    (id, i) =>
                        `--${boundary}\nContent-Type: application/http\nContent-ID: <item${i}>\n\nGET /gmail/v1/users/me/messages/${id}?format=full\n\n`,
                )
                .join('') + `--${boundary}--`

        const response = await fetch(batchEndpoint, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': `multipart/mixed; boundary=${boundary}`,
            },
            body,
        })
        if (!response.ok) {
            throw new Error(
                `Gmail batch API error: ${response.status} ${response.statusText}`,
            )
        }
        const text = await response.text()
        const parts = text.split(/^--batch_.*\r?\n/m)
        const messages: Array<GmailFullMessageResponse | null> = []
        for (const part of parts) {
            const httpBodyMatch = part.match(/HTTP\/1\.1 [^\n]+\n([\s\S]*)/)
            if (!httpBodyMatch) {
                messages.push(null)
                continue
            }
            const bodySection = httpBodyMatch[1]
            const jsonStart = bodySection.indexOf('{')
            const jsonEnd = bodySection.lastIndexOf('}')
            if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
                try {
                    const json = JSON.parse(
                        bodySection.slice(jsonStart, jsonEnd + 1),
                    )
                    messages.push(decodeMessageBodies(json))
                } catch {
                    messages.push(null)
                }
            } else {
                messages.push(null)
            }
        }
        return messages
    }

    const allResults: Array<GmailFullMessageResponse | null> = []
    for (let i = 0; i < params.messageIds.length; i += BATCH_LIMIT) {
        const chunk = params.messageIds.slice(i, i + BATCH_LIMIT)
        const boundary = `${boundaryPrefix}_${i}`
        const chunkResults = await sendBatch(chunk, boundary)
        allResults.push(...chunkResults)
    }
    return allResults
}
